import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, MapPin, Loader2, Clock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

/** PJP Today — mobile screen showing all planned outlets with status chips. */
export default function PJPToday() {
  const nav = useNavigate();
  const [plan, setPlan] = useState(null);
  const [skipTarget, setSkipTarget] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pjp/today");
      setPlan(data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const counts = plan?.counts || {};
  return (
    <div className="p-4 pb-24 space-y-4" data-testid="pjp-today-screen">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-400">Today's route</div>
        <h1 className="text-2xl font-extrabold text-neutral-900">Route plan</h1>
        <div className="text-xs font-mono text-neutral-500">{plan?.date || ""}</div>
      </div>

      <div className="grid grid-cols-4 gap-2 text-center">
        {["planned", "done", "pending", "skipped"].map((k) => (
          <div key={k} className={`rounded-xl p-2 ${
            k === "done" ? "bg-green-50 text-green-800" :
            k === "pending" ? "bg-[#E5197F]/10 text-[#E5197F]" :
            k === "skipped" ? "bg-neutral-100 text-neutral-500" :
            "bg-neutral-50 text-neutral-800"}`} data-testid={`pjp-count-${k}`}>
            <div className="text-lg font-extrabold">{counts[k] || 0}</div>
            <div className="text-[10px] uppercase tracking-widest opacity-70">{k}</div>
          </div>
        ))}
      </div>

      {loading && <div className="py-6 text-center text-neutral-400"><Loader2 className="h-5 w-5 inline animate-spin" /></div>}

      <div className="space-y-2" data-testid="pjp-list">
        {(plan?.items || []).map((it) => (
          <PJPRow key={it.retailer_id} item={it}
                  onStart={() => nav(`/visits/start/${it.retailer_id}`)}
                  onView={() => it.visit_id && nav(`/visits/${it.visit_id}/in-progress`)}
                  onSkip={() => setSkipTarget(it)} />
        ))}
        {plan?.items?.length === 0 && (
          <div className="py-10 text-center text-neutral-400 text-sm">
            No planned outlets today. Contact your ASM.
          </div>
        )}
      </div>

      {skipTarget && (
        <SkipDialog item={skipTarget} onClose={() => setSkipTarget(null)}
                    onDone={() => { setSkipTarget(null); load(); }} />
      )}
    </div>
  );
}

function PJPRow({ item, onStart, onView, onSkip }) {
  const st = item.visit_status_today;
  const chip = {
    pending: { bg: "bg-neutral-100 text-neutral-500", label: "Pending" },
    in_progress: { bg: "bg-yellow-100 text-yellow-800", label: "In progress" },
    done: { bg: "bg-green-100 text-green-800", label: "Done" },
    skipped: { bg: "bg-red-100 text-red-700", label: "Skipped" },
    auto_skipped: { bg: "bg-neutral-100 text-neutral-500", label: "Auto-skipped" },
  }[st] || { bg: "bg-neutral-100 text-neutral-500", label: st };

  return (
    <Card className="border-neutral-200 rounded-2xl" data-testid={`pjp-row-${item.retailer_id.slice(0, 8)}`}>
      <CardContent className="p-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-neutral-400">{item.retailer_code}</div>
          <div className="text-sm font-semibold text-neutral-900 truncate">{item.retailer_name}</div>
          <div className="text-[11px] text-neutral-500 mt-1 flex items-center gap-1">
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] uppercase font-semibold ${chip.bg}`}>
              {chip.label}
            </span>
            {!item.has_location && (
              <span className="text-[10px] text-amber-700">📍 no GPS</span>
            )}
          </div>
        </div>
        {st === "pending" && (
          <Button size="sm" onClick={onStart}
                  className="tulsi-gradient text-white h-9 px-3 rounded-lg"
                  data-testid={`pjp-start-${item.retailer_id.slice(0, 8)}`}>
            Start <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        )}
        {st === "in_progress" && (
          <Button size="sm" onClick={onView} variant="outline"
                  className="h-9 px-3 rounded-lg"
                  data-testid={`pjp-view-${item.retailer_id.slice(0, 8)}`}>
            View
          </Button>
        )}
        {(st === "pending" || st === "auto_skipped") && (
          <Button size="sm" variant="ghost" onClick={onSkip}
                  className="text-red-600 h-9 px-2 text-[11px]"
                  data-testid={`pjp-skip-${item.retailer_id.slice(0, 8)}`}>
            Skip
          </Button>
        )}
        {st === "done" && (
          <CheckCircle2 className="h-5 w-5 text-green-600" />
        )}
      </CardContent>
    </Card>
  );
}

function SkipDialog({ item, onClose, onDone }) {
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/lookups", { params: { category: "visit_skip_reason" } })
       .then(({ data }) => setReasons(data.items || []));
  }, []);

  const submit = async () => {
    if (!reason) return toast.error("Choose a reason");
    setBusy(true);
    try {
      await api.post(`/pjp/${item.pjp_id}/skip`, {
        retailer_id: item.retailer_id, skip_reason: reason, notes: notes || null,
      });
      toast.success("Outlet marked skipped");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="pjp-skip-dialog">
        <DialogHeader><DialogTitle>Skip {item.retailer_name}?</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="pjp-skip-reason"><SelectValue placeholder="Reason…" /></SelectTrigger>
              <SelectContent>
                {reasons.map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add notes (optional)" data-testid="pjp-skip-notes" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-red-600 text-white hover:bg-red-700"
                  data-testid="pjp-skip-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Skip
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
